import { Controller } from "stimulus";

// Spec §7 -- the staff new-bike-reservation form's Pickup Time list.
//
// The list is the branch's arrival slots with their counts, and it depends on
// the branch *and* the date, both of which change inside the form. The server
// paints the first list (so the form works with JS off); this re-fetches it
// from the same public endpoint the widget uses, GET /api/v3/arrival_slots.
//
// Soft cap (C3): counts ride in the label, nothing is ever `disabled`, and the
// save always proceeds. The customer-facing widget is the strict one.
//
// Stimulus is 1.1 here, so: `data-target="arrival-slot-options.foo"` (not
// `data-arrival-slot-options-target`) and `this.data.get(...)` (there is no
// `static values` API until Stimulus 2).
export default class extends Controller {
  static targets = ["branch", "date", "time"];

  connect() {
    // Bumped per request so a slow answer for the branch the staffer has
    // already moved on from cannot overwrite a newer list.
    this.requestId = 0;
  }

  refresh() {
    if (!this.hasTimeTarget || !this.hasBranchTarget) return;

    const branchId = this.branchTarget.value;
    if (!branchId) return;

    // The endpoint is strict about YYYY-MM-DD. flatpickr's default dateFormat
    // is exactly that, but never send anything else if the field is holding
    // something unexpected -- and mirror the server's own fallback (spec §7:
    // "params[:start_date] if present, else today") when it is empty, so
    // changing branch before picking a date still updates the counts.
    const date = this.selectedDate();
    if (!date) return;

    const requestId = ++this.requestId;

    fetch(`${this.data.get("url")}?branch_id=${encodeURIComponent(branchId)}&date=${encodeURIComponent(date)}`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((body) => {
        if (requestId !== this.requestId) return; // a later change already answered
        this.renderOptions(body.arrivalSlots || []);
      })
      .catch(() => {
        // Leave whatever is on screen. Never hand the staffer an empty dropdown.
      });
  }

  selectedDate() {
    const value = this.hasDateTarget ? this.dateTarget.value.trim() : "";
    if (!value) return this.today();

    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
  }

  // Local YYYY-MM-DD. Deliberately not toISOString(), which is UTC and rolls
  // the date over for evening-local users (spec §13.5).
  today() {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, "0");
    const day = `${now.getDate()}`.padStart(2, "0");

    return `${now.getFullYear()}-${month}-${day}`;
  }

  renderOptions(slots) {
    const select = this.timeTarget;
    const previous = select.value;
    const blank = select.options.length && select.options[0].value === "" ? select.options[0].text : null;
    // An empty answer is a branch with no arrival slots: back to the list the
    // form has always hard-coded (spec §3.4).
    const choices = slots.length ? slots.map((slot) => [this.optionLabel(slot), slot.time]) : this.fallbackChoices();

    select.innerHTML = "";
    if (blank !== null) select.appendChild(this.buildOption(blank, ""));
    choices.forEach(([label, value]) => select.appendChild(this.buildOption(label, value)));

    // Keep what the staffer had picked if the new list still offers it.
    if (previous && choices.some(([, value]) => value === previous)) {
      select.value = previous;
    } else {
      select.selectedIndex = 0;
    }
  }

  buildOption(label, value) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = label;
    // No `disabled`, ever: the staff cap is soft (C3).
    return option;
  }

  // "9:00 AM (1/2)" / "11:00 AM (2/2, full)" / "1:00 PM (3/2, over)" -- the same
  // labels ArrivalSlotsHelper#arrival_time_label paints server-side.
  optionLabel(slot) {
    const counts = `${slot.reservationsCount}/${slot.maxReservations}`;
    if (this.overCapacity(slot)) return `${slot.time} (${counts}, over)`;
    if (slot.full) return `${slot.time} (${counts}, full)`;

    return `${slot.time} (${counts})`;
  }

  // `full` is the server's answer (ArrivalSlotAvailability owns that rule);
  // "over" is not on the wire, because the public payload is shared with the
  // customer widget, which shows no counts at all. It is the same arithmetic
  // over fields the payload does carry.
  overCapacity(slot) {
    const overReservations = slot.reservationsCount > slot.maxReservations;
    const overRenters = slot.maxRenters != null && slot.rentersCount > slot.maxRenters;

    return overReservations || overRenters;
  }

  fallbackChoices() {
    return JSON.parse(this.data.get("fallback") || "[]").map((time) => [time, time]);
  }
}
